// 移动版 /mm 全套文案：zh 为完整基准（沿用现有页面文案），en 逐 key 对齐
// 动态数值用 {count}/{n}/{name} 占位，渲染时 replace
export interface MmDict {
    meta: {
        home: { title: string; description: string }
        gamelist: { title: string; description: string }
        contact: { title: string; description: string }
        app: { title: string; description: string }
        gameNotFound: string
    }
    chrome: {
        menuAria: string
        tabs: { home: string; games: string; contact: string; app: string }
        menu: { games: string; cases: string; developers: string; contact: string; app: string }
        language: string
    }
    // 精简页脚（MMFooter）：导航链接列 + 品牌行；备案号/51.la 脚本为固定文案不入字典
    footer: {
        nav: { home: string; games: string; solutions: string; contact: string }
        tagline: string
    }
    home: {
        badge: string
        slogans: { title: string; sub: string; desc: string }[]
        // 双 CTA：主按钮 免费获取方案 → /mm/contact，次按钮 查看全部游戏 → /mm/gamelist（路由固定在组件内）
        ctaPrimary: string
        ctaSecondary: string
        // 数据背书：games/categories 数字读库由 page.tsx 传入（此处只有后缀/标签），languages/nodes 为固定文案
        stats: {
            games: { suffix: string; label: string }
            categories: { suffix: string; label: string }
            languages: { value: string; label: string }
            nodes: { value: string; label: string }
        }
        hot: { eyebrow: string; title: string; all: string }
        scenes: { eyebrow: string; title: string; items: { title: string; desc: string }[] }
        global: {
            eyebrow: string
            title: string
            desc: string
            stats: { countries: string; nodes: string; latency: string }
            hub: string
            regions: string[]
        }
        cta: { title: string; desc: string; button: string }
    }
    gamelist: {
        badge: string
        title: string
        desc: string
        all: string
        blocked: string
    }
    detail: {
        screenshots: string
        screenshotAlt: string
        intro: string
        region: string
        scenarios: string
        languages: string
        languageNotePre: string
        languageNoteLink: string
        languageNotePost: string
        disclaimer: string
        cta: string
        // 页底相关推荐 + 上一款/下一款导航
        relatedTitle: string
        prev: string
        next: string
        actions: {
            play: string
            playVideo: string
            gameVideo: string
            coverTitle: string
            contact: string
            resume: string
        }
        /** "海外"游戏在大陆 IP 下的地区不可用提示（RegionGate） */
        regionBlock: {
            title: string
            desc: string
            back: string
        }
    }
    contact: {
        badge: string
        titlePre: string
        titleAccent: string
        desc: string
        form: {
            title: string
            requiredPre: string
            requiredPost: string
            name: string
            tel: string
            email: string
            company: string
            nameRequired: string
            telRequired: string
            submit: string
            submitting: string
            success: string
            fail: string
        }
        direct: {
            title: string
            phoneLabel: string
            emailLabel: string
            wechat: string
            wechatAlt: string
        }
        promises: { title: string; desc: string }[]
        faq: { title: string; desc: string; items: { q: string; a: string }[] }
    }
    app: {
        badge: string
        title: string
        desc: string
        download: string
        scanHint: string
        qrAlt: string
        whyTitle: string
        features: { title: string; desc: string }[]
        cta: { title: string; desc: string; contact: string; browse: string }
    }
}

export const mmDict: { zh: MmDict; en: MmDict } = {
    zh: {
        meta: {
            home: {
                title: 'YYGAME.TECH - 万物皆可游戏',
                description: '聚焦休闲游戏创新研发，GI深度融合，定制游戏研发，精准匹配客户场景，共筑盈利新高峰！',
            },
            gamelist: {
                title: '全部游戏 - YYGAME.TECH',
                description: '全品类休闲游戏生态，即点即玩，支持定制化接入',
            },
            contact: {
                title: '联系我们 - YYGAME.TECH',
                description: '我们会为您提供最专业的产品解决方案建议，帮助您获取最佳的产品组合',
            },
            app: {
                title: '体验 APP - YYGAME.TECH',
                description: '扫码下载 Demo 应用，抢先体验全部游戏内容',
            },
            gameNotFound: '游戏不存在 - YYGAME.TECH',
        },
        chrome: {
            menuAria: '菜单',
            tabs: { home: '主页', games: '游戏', contact: '联系', app: 'APP' },
            menu: { games: '游戏列表', cases: '客户案例', developers: '开发者', contact: '联系我们', app: '体验APP' },
            language: '语言',
        },
        footer: {
            nav: { home: '主页', games: '全部游戏', solutions: '运营方案', contact: '联系我们' },
            tagline: '© YYGAME.TECH · The best mini games you will find',
        },
        home: {
            badge: 'GI 游戏化互动引擎',
            slogans: [
                {
                    title: '万物皆可游戏',
                    sub: '聚焦休闲游戏创新研发，搭建极速合作通道',
                    desc: 'GI深度融合，定制游戏研发，精准匹配客户场景，共筑盈利新高峰！',
                },
                {
                    title: '让互动成为增长核动力',
                    sub: '全品类休闲游戏生态 + 定制化解决方案',
                    desc: '深度定制服务，解锁商业新场景，小游戏也可以脑洞大开！',
                },
                {
                    title: 'GI引擎赋能全行业',
                    sub: '成就系统 + 虚拟经济 + 社交裂变 三核驱动',
                    desc: '用户停留时长提升130%，社交分享率增长400%！',
                },
            ],
            ctaPrimary: '免费获取方案',
            ctaSecondary: '查看全部游戏',
            stats: {
                games: { suffix: ' 款', label: '服务游戏内容' },
                categories: { suffix: ' 个', label: '游戏内容分类' },
                languages: { value: '12 种', label: '覆盖语种' },
                nodes: { value: '2,800+', label: '全球加速节点' },
            },
            hot: { eyebrow: '热门推荐', title: '热门游戏', all: '全部' },
            scenes: {
                eyebrow: '场景赋能',
                title: '全场景互动赋能',
                items: [
                    { title: '直播伴侣', desc: '弹幕游戏 / 礼物PK / 主播陪玩，深度重构直播间互动体验' },
                    { title: '语音房 2.0', desc: '实时竞技 / 组队闯关 / 成就共享，让语音社交更好玩' },
                    { title: '深度定制', desc: 'GI引擎植入「成就系统+虚拟经济+社交裂变」，解锁商业新场景' },
                ],
            },
            global: {
                eyebrow: '全球网络',
                title: '全球部署 · 服务全球用户',
                desc: '以香港为核心枢纽，加速节点遍布五大洲，智能调度最优链路，低延迟、零卡顿',
                stats: { countries: '覆盖国家与地区', nodes: '全球加速节点', latency: '平均延迟降低' },
                hub: '香港枢纽',
                regions: ['洛杉矶', '伦敦', '法兰克福', '东京', '首尔', '新加坡', '迪拜', '孟买', '圣保罗', '悉尼'],
            },
            cta: {
                title: '准备好开启游戏化增长了吗？',
                desc: '定制游戏研发 · 精准匹配客户场景 · 共筑盈利新高峰',
                button: '免费获取方案',
            },
        },
        gamelist: {
            badge: '游戏库 · {count} 款精品',
            title: '全部游戏',
            desc: '全品类休闲游戏生态，即点即玩，支持定制化接入',
            all: '所有游戏',
            blocked: '该游戏无法在中国大陆境内运行',
        },
        detail: {
            screenshots: '游戏截图',
            screenshotAlt: '{name} 截图 {n}',
            intro: '游戏简介',
            region: '适合区域',
            scenarios: '推荐运营玩法',
            languages: '支持语言',
            languageNotePre: '如您需要的游戏语言不在支持列表中，可',
            languageNoteLink: '联系我们',
            languageNotePost: '快速支持！',
            disclaimer: '此demo仅用于游戏演示，部分游戏无法在中国大陆境内/或境外部分国家/地区接入和运营，请按照相应国家/地区可适用的法律法规要求选择适当的游戏接入和运营。',
            cta: '联系商务 获取完整体验',
            relatedTitle: '相关推荐',
            prev: '上一款',
            next: '下一款',
            actions: {
                play: '立即试玩',
                playVideo: '播放玩法视频',
                gameVideo: '玩法视频',
                coverTitle: '联系我们 获得完整体验',
                contact: '联系商务',
                resume: '继续试玩',
            },
            regionBlock: {
                title: '该游戏在您所在地区暂不可用',
                desc: '根据相关国家/地区可适用的法律法规要求，本游戏暂未向您所在的地区开放。',
                back: '返回游戏列表',
            },
        },
        contact: {
            badge: 'Contact Us',
            titlePre: '联系',
            titleAccent: '我们',
            desc: '我们会为您提供最专业的产品解决方案建议，帮助您获取最佳的产品组合',
            form: {
                title: '请填写以下信息，我们会尽快联系您',
                requiredPre: '带 ',
                requiredPost: ' 为必填项',
                name: '姓名',
                tel: '电话',
                email: '邮箱',
                company: '企业名称',
                nameRequired: '请填写姓名',
                telRequired: '请填写电话',
                submit: '提交',
                submitting: '提交中…',
                success: '您的信息我们已收到,稍后我们商务将联系',
                fail: '请稍后重试,或联系商务电话:(+86) 188 0038 2271',
            },
            direct: {
                title: '您也可以直接联系我们',
                phoneLabel: '手机（点击拨打）',
                emailLabel: '邮箱',
                wechat: '扫码加微信 · 咨询在线客服',
                wechatAlt: '微信二维码',
            },
            promises: [
                { title: '1 个工作日内响应', desc: '提交信息后商务团队快速回访' },
                { title: '专属商务对接', desc: '一对一顾问全程跟进合作事宜' },
                { title: '全程技术支持', desc: '接入联调、上线运营技术伴跑' },
            ],
            faq: {
                title: '常见问题',
                desc: '关于合作接入，大家最关心的几个问题',
                items: [
                    { q: '合作接入需要多长时间？', a: '标准 H5 接入最快当天即可完成联调上线；如需 SDK / API 深度对接，通常 3-5 个工作日完成，全程有技术团队协助。' },
                    { q: '支持哪些合作计费模式？', a: '支持联运分成、游戏时长计费、包月计费三种模式，可根据平台规模与业务节奏灵活选择，后续可随时升级调整。' },
                    { q: '游戏内容是否支持海外地区运营？', a: '支持。游戏覆盖多语言（简中 / 英语 / 日语等），并依托全球加速节点保障海外玩家低延迟体验；具体可运营区域请遵循当地法律法规。' },
                    { q: '是否可以先体验游戏再决定合作？', a: '可以。联系商务后可开通演示环境，先行体验游戏内容与后台能力，确认效果后再正式签约接入。' },
                ],
            },
        },
        app: {
            badge: 'Demo 扫码体验',
            title: '体验 APP',
            desc: '扫码下载 Demo 应用，抢先体验全部游戏内容',
            download: '点击下载',
            scanHint: '或扫码下载体验',
            qrAlt: '{name} 下载二维码',
            whyTitle: '为什么选择 YYGame',
            features: [
                { title: '海量游戏 即点即玩', desc: '{count}+ 款精品休闲游戏全收录，无需逐个下载' },
                { title: '全球加速 低延迟', desc: '依托全球部署加速节点，海内外玩家都能流畅开局' },
                { title: '实时对战 社交裂变', desc: '多人同屏竞技、语音互动，好友组队乐趣翻倍' },
            ],
            cta: {
                title: '想把这些游戏装进你的 App？',
                desc: '联系商务，获取定制化游戏接入方案',
                contact: '联系商务',
                browse: '先看看游戏',
            },
        },
    },
    en: {
        meta: {
            home: {
                title: 'YYGAME.TECH - Everything Can Be a Game',
                description: 'Focused on casual game innovation: GI-powered engagement, custom game development, and precise scenario matching to drive new revenue growth.',
            },
            gamelist: {
                title: 'All Games - YYGAME.TECH',
                description: 'A full-category casual game ecosystem — instant play, custom integration supported',
            },
            contact: {
                title: 'Contact Us - YYGAME.TECH',
                description: 'Professional product solution advice to help you find the best game lineup for your business',
            },
            app: {
                title: 'Demo App - YYGAME.TECH',
                description: 'Scan to download the demo app and try every game first-hand',
            },
            gameNotFound: 'Game Not Found - YYGAME.TECH',
        },
        chrome: {
            menuAria: 'Menu',
            tabs: { home: 'Home', games: 'Games', contact: 'Contact', app: 'App' },
            menu: { games: 'Game Library', cases: 'Cases', developers: 'Developers', contact: 'Contact Us', app: 'Demo App' },
            language: 'Language',
        },
        footer: {
            nav: { home: 'Home', games: 'All Games', solutions: 'Solutions', contact: 'Contact Us' },
            tagline: '© YYGAME.TECH · The best mini games you will find',
        },
        home: {
            badge: 'GI Gamification Engine',
            slogans: [
                {
                    title: 'Everything Can Be a Game',
                    sub: 'Casual game innovation with a fast-track partnership channel',
                    desc: 'GI-powered integration and custom game development, precisely matched to your scenarios — reach new revenue heights together!',
                },
                {
                    title: 'Interaction as a Growth Engine',
                    sub: 'A full-category casual game ecosystem + custom solutions',
                    desc: 'Deep customization unlocks new business scenarios — even mini games can be wildly creative!',
                },
                {
                    title: 'GI Engine for Every Industry',
                    sub: 'Achievements + virtual economy + social virality — a triple driver',
                    desc: 'Session time up 130%, social share rate up 400%!',
                },
            ],
            ctaPrimary: 'Get a Free Proposal',
            ctaSecondary: 'Browse All Games',
            stats: {
                games: { suffix: '', label: 'Games Served' },
                categories: { suffix: '', label: 'Content Categories' },
                languages: { value: '12+', label: 'Languages Covered' },
                nodes: { value: '2,800+', label: 'Acceleration Nodes' },
            },
            hot: { eyebrow: 'Hot Picks', title: 'Popular Games', all: 'View All' },
            scenes: {
                eyebrow: 'All Scenarios',
                title: 'Interactive Empowerment',
                items: [
                    { title: 'Live Stream Companion', desc: 'Bullet-chat games / gift PK / host co-play — reimagining live room interaction' },
                    { title: 'Voice Room 2.0', desc: 'Real-time battles / team quests / shared achievements — voice social made more fun' },
                    { title: 'Deep Customization', desc: 'The GI engine embeds achievements, virtual economy and social virality to unlock new business scenarios' },
                ],
            },
            global: {
                eyebrow: '全球网络',
                title: 'Global Deployment · Serving Users Worldwide',
                desc: 'With Hong Kong as the core hub and acceleration nodes across five continents, smart routing picks the optimal link — low latency, zero lag',
                stats: { countries: 'Countries & Regions', nodes: 'Global Edge Nodes', latency: 'Avg. Latency Cut' },
                hub: 'Hong Kong Hub',
                regions: ['Los Angeles', 'London', 'Frankfurt', 'Tokyo', 'Seoul', 'Singapore', 'Dubai', 'Mumbai', 'São Paulo', 'Sydney'],
            },
            cta: {
                title: 'Ready to Grow with Gamification?',
                desc: 'Custom game development · Precise scenario matching · Shared revenue growth',
                button: 'Get a Free Proposal',
            },
        },
        gamelist: {
            badge: 'Game Library · {count} Titles',
            title: 'All Games',
            desc: 'A full-category casual game ecosystem — instant play, custom integration supported',
            all: 'All Games',
            blocked: 'This game is not available in mainland China',
        },
        detail: {
            screenshots: 'Screenshots',
            screenshotAlt: '{name} screenshot {n}',
            intro: 'About',
            region: 'Regions',
            scenarios: 'Best Scenarios',
            languages: 'Languages',
            languageNotePre: 'Need a language not listed? ',
            languageNoteLink: 'Contact us',
            languageNotePost: ' for quick support!',
            disclaimer: 'This demo is for presentation only. Some games may not be available for integration or operation in mainland China or certain overseas countries/regions. Please choose games in compliance with the laws and regulations of the applicable country/region.',
            cta: 'Contact Sales for Full Access',
            relatedTitle: 'Related Games',
            prev: 'Previous',
            next: 'Next',
            actions: {
                play: 'Play Now',
                playVideo: 'Watch Gameplay',
                gameVideo: 'Gameplay Video',
                coverTitle: 'Contact Us for Full Access',
                contact: 'Contact Sales',
                resume: 'Keep Playing',
            },
            regionBlock: {
                title: 'This game is not available in your region',
                desc: 'In compliance with the applicable laws and regulations, this game is not currently offered in your region.',
                back: 'Back to Games',
            },
        },
        contact: {
            badge: 'Contact Us',
            titlePre: 'Contact ',
            titleAccent: 'Us',
            desc: 'We provide professional product solution advice to help you find the best product mix',
            form: {
                title: 'Leave your details and we will reach out shortly',
                requiredPre: 'Fields marked ',
                requiredPost: ' are required',
                name: 'Name',
                tel: 'Phone',
                email: 'Email',
                company: 'Company',
                nameRequired: 'Please enter your name',
                telRequired: 'Please enter your phone number',
                submit: 'Submit',
                submitting: 'Submitting…',
                success: 'We have received your information. Our sales team will contact you shortly.',
                fail: 'Please try again later, or call sales at (+86) 188 0038 2271',
            },
            direct: {
                title: 'Or reach us directly',
                phoneLabel: 'Phone (tap to call)',
                emailLabel: 'Email',
                wechat: 'Scan to add us on WeChat · Online support',
                wechatAlt: 'WeChat QR code',
            },
            promises: [
                { title: 'Response Within 1 Business Day', desc: 'Our sales team follows up quickly after you submit' },
                { title: 'Dedicated Account Manager', desc: 'A one-on-one consultant follows your partnership throughout' },
                { title: 'Full Technical Support', desc: 'Technical companion from integration and testing to launch and operations' },
            ],
            faq: {
                title: 'FAQ',
                desc: 'The most common questions about partnering with us',
                items: [
                    { q: 'How long does integration take?', a: 'Standard H5 integration can go live as fast as the same day. Deep SDK/API integration typically takes 3-5 business days, with our technical team assisting throughout.' },
                    { q: 'Which billing models are supported?', a: 'Revenue share, play-time billing, and monthly plans. Choose flexibly based on your platform scale and business pace, and upgrade anytime.' },
                    { q: 'Can the games operate overseas?', a: 'Yes. Games cover multiple languages (Simplified Chinese, English, Japanese, etc.), backed by global acceleration nodes for low-latency play. Please follow local laws and regulations for operating regions.' },
                    { q: 'Can we try the games before committing?', a: 'Absolutely. Contact sales to open a demo environment and experience the games and dashboard capabilities before signing.' },
                ],
            },
        },
        app: {
            badge: 'Scan to Try the Demo',
            title: 'Demo App',
            desc: 'Scan to download the demo app and be the first to try all games',
            download: 'Download',
            scanHint: 'or scan the QR code',
            qrAlt: '{name} QR code',
            whyTitle: 'Why YYGame',
            features: [
                { title: 'Huge Library, Instant Play', desc: 'All {count}+ quality casual games in one place — no separate downloads' },
                { title: 'Global Acceleration, Low Latency', desc: 'Global edge nodes keep gameplay smooth for players at home and abroad' },
                { title: 'Real-Time Battles & Social Virality', desc: 'Multiplayer competition and voice interaction — double the fun with friends' },
            ],
            cta: {
                title: 'Want These Games in Your App?',
                desc: 'Contact sales for a tailored game integration plan',
                contact: 'Contact Sales',
                browse: 'Browse Games First',
            },
        },
    },
}
